// Remembering that someone has put a warning away (MDL-393).
//
// This is a per-viewer display preference, not device state, so it lives in
// localStorage rather than the config. Writing it to the device would mean
// mutating the very thing being warned about, and would push one person's
// choice onto every other editor that opens that device.
//
// Two properties matter more than the storage mechanism:
//
//   - Dismissal is keyed by *state as well as device*. Putting away "you are
//     still on the shipped passphrase" must never silence "this network has no
//     passphrase at all" — they are different conditions, and the second is
//     worse. Keying only by device would hide the upgrade in severity.
//
//   - Every access is wrapped. localStorage throws in private browsing and
//     returns nothing after cleared site data, and a warning that fails to
//     render because its dismissal check exploded is worse than one that
//     shows again. Unavailable storage therefore reads as "not dismissed".

const KEY_PREFIX = 'midulate.apWarning.dismissed';

/**
 * Storage key for one (state, device) pair.
 *
 * `deviceKey` is whatever identifies the device to this editor — a base URL is
 * enough. It may be null: the on-device editor is served *by* the device, so
 * its localStorage origin is already per-device and needs no further scoping.
 *
 * Note two devices both reached at the AP address share a key, since in AP
 * mode the address is always the same. You can only be joined to one at a
 * time, so in practice that is the device in front of you.
 */
export function warningStorageKey(state, deviceKey = null) {
  return `${KEY_PREFIX}.${state}.${deviceKey || 'any'}`;
}

/** True only when a dismissal was definitely recorded. */
export function isWarningDismissed(state, deviceKey = null, storage = defaultStorage()) {
  if (!state) return false;
  try {
    return storage?.getItem(warningStorageKey(state, deviceKey)) === '1';
  } catch {
    // Private browsing, blocked site data, or no storage at all — show it.
    return false;
  }
}

/** Record a dismissal. Silently does nothing if storage is unavailable. */
export function dismissWarning(state, deviceKey = null, storage = defaultStorage()) {
  if (!state) return;
  try {
    storage?.setItem(warningStorageKey(state, deviceKey), '1');
  } catch {
    // The warning still hides for this session; it returns on reload. That is
    // the right failure: nothing is lost, and nothing pretends to persist.
  }
}

function defaultStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null; // accessing the property itself can throw
  }
}
